import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, FolderKanban, MessageSquare, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminHeader, EmptyState } from "@/pages/admin/AdminLayout";
import { ProgressBar, StatusBadge, UnreadBadge, fmtDue } from "@/components/project/ProjectBits";

const ProjectCard = ({ p, i }) => (
  <Link to={`/portal/projects/${p.id}`} data-testid={`portal-project-card-${p.id}`}
    className="card-tech rise group block rounded-2xl p-6 hover:-translate-y-0.5" style={{ animationDelay: `${i * 70}ms` }}>
    <div className="flex items-start justify-between gap-3">
      <h2 data-testid={`portal-project-name-${p.id}`} className="text-lg font-semibold text-white group-hover:text-cyan-200">{p.name}</h2>
      <StatusBadge status={p.status} testId={`portal-project-status-${p.id}`} />
    </div>
    {p.description && <p className="mt-2 line-clamp-2 text-sm text-slate-400">{p.description}</p>}
    <div className="mt-5"><ProgressBar value={p.progress} testId={`portal-project-progress-${p.id}`} /></div>
    <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-400">
      <span data-testid={`portal-project-due-${p.id}`} className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> {fmtDue(p.due_date)}</span>
      <span className="inline-flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> {p.file_count} {p.file_count === 1 ? "file" : "files"}</span>
      <span className="inline-flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Messages <UnreadBadge count={p.unread_count} testId={`portal-project-unread-${p.id}`} /></span>
    </div>
  </Link>
);

export default function PortalProjects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch((e) => { setProjects([]); toast.error(formatApiErrorDetail(e.response?.data?.detail)); });
  }, []);

  return (
    <div data-testid="portal-projects-page">
      <AdminHeader eyebrow="// client portal" title={`Welcome, ${(user?.name || "").split(" ")[0] || "there"}`}>
        {user?.company ? `${user.company} · ` : ""}Your projects, files and messages in one place.
      </AdminHeader>
      {projects === null ? <div className="h-40 animate-pulse rounded-2xl bg-white/[0.03]" /> : projects.length === 0 ? (
        <EmptyState icon={FolderKanban} title="No projects yet — we'll set one up for you soon." testId="portal-projects-empty-state" />
      ) : (
        <div data-testid="portal-projects-grid" className="grid gap-6 md:grid-cols-2">
          {projects.map((p, i) => <ProjectCard key={p.id} p={p} i={i} />)}
        </div>
      )}
    </div>
  );
}
